import { useId } from 'react';
import { styled } from '@/styles/stitches.config';
import { ComposableStyledComponentProps } from '@/types/composable-styled-component';
import { useMediaQuery } from '@/utils/use-media-query';
import { scaleRadial } from 'd3-scale';
import { AnimationControls, motion, SVGMotionProps, useAnimation } from 'framer-motion';
import React, { SVGAttributes, useCallback, useEffect, useMemo } from 'react';
import { isPresent } from 'ts-is-present';
import { ChartRoot, ChartSvg } from '../chart';
import { Legend } from './legend';

export type SunriseChartD = { label: string; value: number; isApproximatValue?: boolean };

export type SunriseChartDataGroup = {
  label: string;
  data: SunriseChartD[];
};

export type SunriseChartData = [
  top?: SunriseChartDataGroup,
  bottom?: Partial<Pick<SunriseChartDataGroup, 'label'>> & Omit<SunriseChartDataGroup, 'label'>
];

export interface SunriseChartProps extends ComposableStyledComponentProps<HTMLDivElement> {
  data?: SunriseChartData;
  formatValue: (value: number) => string;
}

// Dependency injection context for components deeper in the tree. Avoids prop drilling.
export const ChartContext = React.createContext({
  formatValue: (n: number) => String(n),
  getFill: (hasFill: boolean, isFirstGroup: boolean): string => {
    if (hasFill) {
      return isFirstGroup ? 'var(--color-primary)' : 'var(--color-secondary)';
    }

    return 'none';
  },
  getStroke: (hasStroke: boolean): string => {
    if (hasStroke) return 'var(--color-text-light)';
    return 'none';
  },
});

const Centered = styled('g', {
  transformOrigin: '50% 50%',
  transition: 'transform 0.2s ease-in-out',
});

export function SunriseChart(props: SunriseChartProps) {
  const { data, css, formatValue } = props;

  const getFill = useCallback((hasFill: boolean, isFirstGroup: boolean): string => {
    if (hasFill) {
      return isFirstGroup ? 'var(--color-primary)' : 'var(--color-secondary)';
    }

    return 'none';
  }, []);

  const getStroke = useCallback((hasStroke: boolean): string => {
    if (hasStroke) return 'var(--color-text-light)';
    return 'none';
  }, []);

  const dependencies = useMemo(
    () => ({ formatValue, getFill, getStroke }),
    [formatValue, getFill, getStroke]
  );

  const topClipPathId = useId();
  const bottomClipPathId = useId();

  const circleControls = useAnimation();

  // This hooks runs animations when the appropriate props change.
  // That means there are some props in the dependencies array that aren't used in the hook.
  useEffect(() => {
    const sequence = async () => {
      await circleControls.start((r) => ({ r }));
    };

    sequence();
  }, [circleControls, data]);

  const isCenterAligned = useMediaQuery('(min-width: 67.5em)');

  return (
    <ChartContext.Provider value={dependencies}>
      <ChartRoot
        css={css}
        rootCss={{
          verticalAlign: isCenterAligned ? 'center' : 'flex-end',
        }}
      >
        <ChartSvg
          viewBox="0 0 100 100"
          css={{
            '&:not(:root)': {
              overflow: 'visible',
            },
          }}
        >
          <defs>
            <clipPath id={topClipPathId}>
              <rect x="0" y="0" height="49.5" width="100" fill="black" />
            </clipPath>
            <clipPath id={bottomClipPathId}>
              <rect x="0" y="50.5" height="50" width="100" fill="black" />
            </clipPath>
          </defs>

          <Centered
            css={{
              transform: data?.[1] ? 'translateY(0%) rotate(-45deg)' : `translateY(16.667%)`,
            }}
          >
            {/* This line is supposed to overflow the SVG for a visual effect. */}
            <line x1="-300" x2="400" y1="50" y2="50" stroke="var(--color-text-light)" />

            {data &&
              data
                .filter(isPresent)
                .map((group, groupIdx) =>
                  group.data.map((d, dIdx) => (
                    <Circle
                      key={`${groupIdx}-${dIdx}`}
                      animationControls={circleControls}
                      value={d.value}
                      clipPathId={groupIdx === 0 ? topClipPathId : bottomClipPathId}
                      fill={getFill(dIdx === 0, groupIdx === 0)}
                      stroke={getStroke(dIdx === 1)}
                    />
                  ))
                )}
          </Centered>
        </ChartSvg>

        <Legend data={data} />
      </ChartRoot>
    </ChartContext.Provider>
  );
}

type CircleProps = SVGAttributes<SVGCircleElement> &
  SVGMotionProps<SVGCircleElement> & {
    animationControls: AnimationControls;
    clipPathId?: string;
    value?: number;
  };

function Circle(props: CircleProps) {
  const { animationControls, value, clipPathId, ...rest } = props;

  const r = scaleRadial().domain([0, 1]).range([0, 30]);

  return (
    <motion.circle
      initial={{ r: 0 }}
      animate={animationControls}
      transition={{ type: 'spring', duration: 0.2 }}
      custom={value ? r(value) : 0}
      cx="50"
      cy="50"
      clipPath={`url(#${clipPathId})`}
      strokeDasharray={'4 4'}
      {...rest}
    />
  );
}
